import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLTableStyling = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Tables & Data</div>
                <h1>Table Accessibility & Semantic Tables</h1>
                <p className="hero-lead">
                    Create accessible, screen-reader-friendly tables. Master ARIA attributes, semantic
                    structure, and techniques for inclusive data presentation.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Table Accessibility Matters</h2>
                <p>
                    Screen reader users navigate tables differently. Proper semantic structure and ARIA
                    attributes help them understand table relationships and data context.
                </p>

                <div className="accessibility-stats">
                    <div className="stat-card">
                        <h3>15%</h3>
                        <p>of population has some form of disability</p>
                    </div>
                    <div className="stat-card">
                        <h3>2.2B</h3>
                        <p>people worldwide with vision impairment</p>
                    </div>
                    <div className="stat-card">
                        <h3>Legal</h3>
                        <p>requirement in many countries (ADA, WCAG)</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Accessible Table Essentials</h2>

                <h3>1. Always Use &lt;caption&gt;</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<table>
    <caption>Monthly Sales Report - Q4 2024</caption>
    <thead>
        <tr>
            <th>Month</th>
            <th>Sales</th>
            <th>Growth</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <td>October</td>
            <td>$45,000</td>
            <td>+12%</td>
        </tr>
    </tbody>
</table>

<!-- Screen reader announces: "Table: Monthly Sales Report - Q4 2024" --&gt;`} />
                </div>

                <h3>2. Use &lt;th&gt; for Headers</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span> Bad: Using &lt;td&gt; for headers</span>
                    </div>
                    <CodeBlock language="html" code={`<tr>
    <td><strong>Name</strong></td>
    <td><strong>Age</strong></td>
</tr>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Good: Using &lt;th&gt;</span>
                    </div>
                    <CodeBlock language="html" code={`<tr>
    <th>Name</th>
    <th>Age</th>
</tr>`} />
                </div>

                <h3>3. Add scope Attribute</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<table>
    <thead>
        <tr>
            <th scope="col">Product</th>
            <th scope="col">Price</th>
            <th scope="col">Stock</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <th scope="row">Laptop</th>
            <td>$999</td>
            <td>15</td>
        </tr>
        <tr>
            <th scope="row">Mouse</th>
            <td>$25</td>
            <td>150</td>
        </tr>
    </tbody>
</table>

<!-- Screen reader announces: --&gt;
<!-- "Laptop, Price: $999" --&gt;
<!-- "Laptop, Stock: 15" --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complex Table Accessibility</h2>

                <h3>Using id and headers</h3>
                <p>For complex tables with multiple header levels:</p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<table>
    <caption>Sales by Region and Quarter</caption>
    <thead>
        <tr>
            <th id="region">Region</th>
            <th id="q1" colspan="2">Q1</th>
            <th id="q2" colspan="2">Q2</th>
        </tr>
        <tr>
            <th id="blank"></th>
            <th id="q1-units" headers="q1">Units</th>
            <th id="q1-rev" headers="q1">Revenue</th>
            <th id="q2-units" headers="q2">Units</th>
            <th id="q2-rev" headers="q2">Revenue</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <th id="north" headers="region">North</th>
            <td headers="north q1 q1-units">150</td>
            <td headers="north q1 q1-rev">$150k</td>
            <td headers="north q2 q2-units">180</td>
            <td headers="north q2 q2-rev">$180k</td>
        </tr>
        <tr>
            <th id="south" headers="region">South</th>
            <td headers="south q1 q1-units">120</td>
            <td headers="south q1 q1-rev">$120k</td>
            <td headers="south q2 q2-units">140</td>
            <td headers="south q2 q2-rev">$140k</td>
        </tr>
    </tbody>
</table>

<!-- Screen reader announces: --&gt;
<!-- "North, Q1 Units: 150" --&gt;
<!-- "North, Q1 Revenue: $150k" --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>ARIA Attributes for Tables</h2>

                <h3>role="table"</h3>
                <p>When using div-based tables (not recommended, but sometimes necessary):</p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<div role="table" aria-label="Employee Directory">
    <div role="rowgroup">
        <div role="row">
            <div role="columnheader">Name</div>
            <div role="columnheader">Position</div>
        </div>
    </div>
    <div role="rowgroup">
        <div role="row">
            <div role="cell">John Doe</div>
            <div role="cell">Developer</div>
        </div>
    </div>
</div>

<!-- ARIA roles: --&gt;
<!-- table, rowgroup, row, columnheader, rowheader, cell, gridcell --&gt;`} />
                </div>

                <Warning><strong> Important:</strong> Always prefer native HTML table elements. Only use
                    ARIA roles when absolutely necessary (e.g., complex interactive grids).</Warning>

                <h3>aria-label and aria-describedby</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<table aria-label="2024 Sales Performance">
    <caption id="sales-caption">
        Sales performance across all regions for 2024
    </caption>
    <!-- Table content --&gt;
</table>

<!-- With description --&gt;
<p id="table-desc">
    This table shows year-over-year comparison. 
    Use arrow keys to navigate.
</p>

<table aria-describedby="table-desc">
    <!-- Table content --&gt;
</table>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Sortable & Interactive Tables</h2>

                <h3>Accessible Sort Buttons</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<table>
    <thead>
        <tr>
            <th>
                <button aria-label="Sort by name" 
                        aria-sort="none"
                        onclick="sort(this, 0)">
                    Name
                    <span aria-hidden="true"></span>
                </button>
            </th>
            <th>
                <button aria-label="Sort by age" 
                        aria-sort="none"
                        onclick="sort(this, 1)">
                    Age
                    <span aria-hidden="true"></span>
                </button>
            </th>
        </tr>
    </thead>
    <tbody>
        <!-- Table rows --&gt;
    </tbody>
</table>

<script>
function sort(button, columnIndex) {
    // Sort logic...
    
    // Update ARIA
    const allButtons = document.querySelectorAll('thead button');
    allButtons.forEach(btn => btn.setAttribute('aria-sort', 'none'));
    
    const sortDir = button.getAttribute('aria-sort') === 'ascending' 
        ? 'descending' 
        : 'ascending';
    button.setAttribute('aria-sort', sortDir);
    
    // Update visual indicator
    button.querySelector('span').textContent = 
        sortDir === 'ascending' ? '' : '';
}
</script>

<!-- aria-sort values: ascending, descending, none, other --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Visual Styling for Accessibility</h2>

                <h3>Color Contrast</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* Good: WCAG AA compliant (4.5:1 contrast ratio) */
table {
    color: #000;
    background: #fff;
}

thead {
    background: #2c3e50; /* Dark blue */
    color: #ffffff; /* White text */
}

/*  Bad: Low contrast */
table {
    color: #999; /* Too light */
    background: #fff;
}
</style>

<!-- Test contrast at: --&gt;
<!-- - WebAIM Contrast Checker --&gt;
<!-- - Chrome DevTools Lighthouse --&gt;`} />
                </div>

                <h3>Focus Indicators</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* Visible focus for keyboard navigation */
button:focus,
a:focus {
    outline: 3px solid #4CAF50;
    outline-offset: 2px;
}

/* Interactive cells */
td:focus {
    outline: 2px solid #2196F3;
    background-color: #E3F2FD;
}

/* Don't remove focus outline! */
/* :focus { outline: none; }  Never do this! */
</style>`} />
                </div>

                <h3>Zebra Striping (With Purpose)</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* Helps users track rows across wide tables */
tbody tr:nth-child(even) {
    background-color: #f8f9fa;
}

tbody tr:nth-child(odd) {
    background-color: #ffffff;
}

/* Hover state */
tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

/* Selected row */
tbody tr.selected {
    background-color: #cce5ff;
    border-left: 4px solid #007bff;
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive & Accessible</h2>

                <h3>Screen Reader Only Text</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0,0,0,0);
    white-space: nowrap;
    border: 0;
}
</style>

<table>
    <caption>
        Product Inventory
        <span class="sr-only">
            This table shows current inventory levels. 
            Products low in stock are highlighted.
        </span>
    </caption>
    <!-- Table content --&gt;
</table>`} />
                </div>

                <h3>Mobile-Friendly & Accessible</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
@media screen and (max-width: 600px) {
    /* Card layout on mobile */
    thead {
        position: absolute;
        left: -10000px;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
    }
    
    tbody tr {
        display: block;
        margin-bottom: 20px;
        border: 2px solid #ddd;
    }
    
    tbody td {
        display: block;
        text-align: right;
        padding: 10px;
        border: none;
    }
    
    tbody td::before {
        content: attr(data-label);
        float: left;
        font-weight: bold;
    }
    
    /* Maintain focus indicators */
    tbody tr:focus,
    tbody td:focus {
        outline: 3px solid #4CAF50;
    }
}
</style>

<table>
    <thead>
        <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
        </tr>
    </thead>
    <tbody>
        <tr tabindex="0">
            <td data-label="Name">John Doe</td>
            <td data-label="Email">john@example.com</td>
            <td data-label="Phone">555-0100</td>
        </tr>
    </tbody>
</table>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Testing for Accessibility</h2>

                <h3>Manual Testing</h3>
                <ul>
                    <li><strong>Keyboard Navigation:</strong> Tab through table, ensure all interactive elements are reachable</li>
                    <li><strong>Screen Reader:</strong> Test with NVDA (Windows), VoiceOver (Mac), or JAWS</li>
                    <li><strong>Zoom:</strong> Test at 200% zoom, ensure content remains readable</li>
                    <li><strong>High Contrast:</strong> Test in high contrast mode</li>
                </ul>

                <h3>Automated Testing Tools</h3>
                <ul>
                    <li><strong>WAVE:</strong> Browser extension for accessibility testing</li>
                    <li><strong>axe DevTools:</strong> Chrome/Firefox extension</li>
                    <li><strong>Lighthouse:</strong> Built into Chrome DevTools</li>
                    <li><strong>Pa11y:</strong> Command-line tool</li>
                </ul>

                <h3>Screen Reader Keyboard Shortcuts</h3>
                <div className="keyboard-shortcuts">
                    <h4>NVDA (Windows):</h4>
                    <ul>
                        <li><kbd>T</kbd> - Next table</li>
                        <li><kbd>Ctrl + Alt + Arrow keys</kbd> - Navigate cells</li>
                        <li><kbd>Insert + F7</kbd> - List all tables</li>
                    </ul>

                    <h4>VoiceOver (Mac):</h4>
                    <ul>
                        <li><kbd>VO + Cmd + T</kbd> - Next table</li>
                        <li><kbd>VO + Arrow keys</kbd> - Navigate cells</li>
                        <li><kbd>VO + U</kbd> - Web rotor (list tables)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Accessible Example</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<table aria-label="Employee Performance Q4 2024">
    <caption>
        Employee Performance Review - Q4 2024
        <span class="sr-only">
            This table shows performance ratings and bonuses. 
            Employees are sorted by department.
        </span>
    </caption>
    
    <colgroup>
        <col style="width: 30%;">
        <col style="width: 20%;">
        <col style="width: 25%;">
        <col style="width: 25%;">
    </colgroup>
    
    <thead>
        <tr>
            <th scope="col" id="name">Employee Name</th>
            <th scope="col" id="dept">Department</th>
            <th scope="col" id="rating">Performance Rating</th>
            <th scope="col" id="bonus">Bonus</th>
        </tr>
    </thead>
    
    <tbody>
        <tr>
            <th scope="row" headers="name">Alice Johnson</th>
            <td headers="dept">Engineering</td>
            <td headers="rating">
                <span aria-label="4 out of 5 stars"></span>
            </td>
            <td headers="bonus">$5,000</td>
        </tr>
        <tr>
            <th scope="row" headers="name">Bob Smith</th>
            <td headers="dept">Marketing</td>
            <td headers="rating">
                <span aria-label="5 out of 5 stars"></span>
            </td>
            <td headers="bonus">$7,500</td>
        </tr>
    </tbody>
    
    <tfoot>
        <tr>
            <th scope="row" colspan="3">Total Bonuses</th>
            <td>$12,500</td>
        </tr>
    </tfoot>
</table>

<style>
table {
    border-collapse: collapse;
    width: 100%;
    margin: 20px 0;
}

caption {
    font-size: 1.2em;
    font-weight: bold;
    margin-bottom: 10px;
    text-align: left;
}

th, td {
    padding: 12px;
    text-align: left;
    border: 1px solid #ddd;
}

thead {
    background-color: #2c3e50;
    color: white;
}

tbody tr:nth-child(even) {
    background-color: #f8f9fa;
}

tbody tr:hover {
    background-color: #e9ecef;
}

tfoot {
    background-color: #ecf0f1;
    font-weight: bold;
}

/* Accessibility */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0,0,0,0);
}

/* Focus indicators */
th:focus, td:focus {
    outline: 3px solid #4CAF50;
    outline-offset: -3px;
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility Checklist</h2>
                <div className="checklist">
                    <ul>
                        <li> <code>&lt;caption&gt;</code> element present</li>
                        <li> <code>&lt;th&gt;</code> used for all headers</li>
                        <li> <code>scope</code> attribute on headers</li>
                        <li> <code>&lt;thead&gt;</code>, <code>&lt;tbody&gt;</code>, <code>&lt;tfoot&gt;</code> used</li>
                        <li> Complex tables use <code>id</code> and <code>headers</code></li>
                        <li> Color contrast meets WCAG AA (4.5:1)</li>
                        <li> Focus indicators visible</li>
                        <li> Keyboard navigable</li>
                        <li> Responsive design</li>
                        <li> Tested with screen reader</li>
                        <li> No tables for layout</li>
                        <li> Clear data relationships</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLTableStyling;



